import { Box, Flex, Text, Badge, HTMLChakraProps, SkeletonText, Skeleton, forwardRef } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { RelativeTimestamp } from '@/components/ui/core/RelativeTimestamp/RelativeTimestamp';
import { ResourceType } from '@/enums/ResourceType';
import { INotification } from '@/resources/upgrade/Notification';

export interface INotificationItemProps extends HTMLChakraProps<'div'> {
  notification: INotification;
}

// Community notification kinds that reference a post title (rendered bold).
// Comment/reply "liked" kinds have no title to highlight.
const COMMUNITY_TITLE_TYPES = new Set([
  'community_post_new_comment',
  'community_post_new_reply',
  'community_post_liked',
  'community_comment_liked',
  'community_reply_liked',
  'community_post_mentioned',
  'community_comment_mentioned',
  'community_reply_mentioned',
]);

// Activity notifications whose copy changes depending on whether the recipient
// owns the post ("…your post") or merely follows it ("…a post you follow").
// Mentions always target "you", so they're intentionally excluded. Each type
// has a `<type>_follow` translation key for the follower variant.
const FOLLOW_VARIANT_TYPES = new Set([
  'community_post_new_comment',
  'community_post_new_reply',
  'community_post_liked',
  'community_comment_liked',
  'community_reply_liked',
]);

export const NotificationItem: FC<INotificationItemProps> = forwardRef<INotificationItemProps, 'div'>(
  function NotificationItem({ notification, ...rest }, ref) {
    const { t } = useTranslation();

    const isLecture = notification.resource_type === ResourceType.Lecture;
    const actor = notification.actor_profile?.full_name ?? '';

    const isFollowedPost = notification.community_post?.meta?.followed === true;

    const messageType =
      isFollowedPost && FOLLOW_VARIANT_TYPES.has(notification.type) ? `${notification.type}_follow` : notification.type;

    // Lead text (not bold). Lecture keeps its original copy; community kinds
    // interpolate the actor's name.
    const message = isLecture ? t('notifications.lecture_chat_comment') : t(`notifications.${messageType}`, { actor });

    // Trailing bold highlight — the lecture title or the community post title.
    const getHighlight = () => {
      if (isLecture) return notification.lecture?.title;
      if (COMMUNITY_TITLE_TYPES.has(notification.type)) return notification.community_post?.title;

      return '';
    };

    const highlight = getHighlight();

    return (
      <Flex
        ref={ref}
        alignItems="center"
        justifyContent="space-between"
        p={3}
        _hover={{ bgColor: 'gray.50' }}
        borderRadius={2}
        {...rest}
      >
        {/* minW 0 lets this flex item shrink below its longest word; overflowWrap then breaks that
            word instead of pushing the panel wider (post titles can be one unbroken string) */}
        <Box mr={{ base: 4, md: 6, lg: 4 }} flex="1 1 100%" minW={0}>
          <Text mb={2} fontSize="sm" lineHeight="5" fontWeight="normal" overflowWrap="anywhere">
            {`${message} `}
            {highlight && (
              <Text as="span" fontWeight="bold">
                {isLecture ? highlight : `“${highlight}”`}
              </Text>
            )}
          </Text>
          <RelativeTimestamp
            as={Text}
            color="gray.700"
            fontSize="xs"
            lineHeight="1rem"
            fontFamily="heading"
            timestamp={new Date(notification.created_at)}
          >
            {(formatedTimestamp) =>
              t('notifications.relativeTimestamp', {
                time: formatedTimestamp,
              })
            }
          </RelativeTimestamp>
        </Box>
        {!notification.read && <Badge size="sm" flex="1 0 auto" />}
      </Flex>
    );
  }
);

export const NotificationItemSkeleton: FC<HTMLChakraProps<'div'>> = (props) => {
  return (
    <Box w="full" p={3} {...props}>
      <SkeletonText noOfLines={3} skeletonHeight={4} mb={4} />
      <Skeleton w={10} h={4} />
    </Box>
  );
};
